import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useBranding } from '../../contexts/BrandingContext';
import { useCRM } from '../../contexts/CRMContext';
import { useNotifications } from '../../contexts/NotificationContext';
import { messagesAPI } from '../../utils/crmApi';
import { globalMessageTracking } from '../../hooks/useMessagePolling';
import {
    ChatBubbleLeftRightIcon, PaperAirplaneIcon, UserIcon, ClockIcon,
    CheckIcon, CheckCircleIcon, ExclamationTriangleIcon, PaperClipIcon,
    EllipsisVerticalIcon, ArchiveBoxIcon, TrashIcon, CalendarIcon,
    MapPinIcon, FaceSmileIcon
} from '@heroicons/react/24/outline';
import { Button } from '../ui/button';
import { calendarAPI } from '../../utils/crmApi';
import toast from 'react-hot-toast';
import EmojiPicker from 'emoji-picker-react';
import { Popover, PopoverTrigger, PopoverContent } from '../ui/popover';

const MessageList = ({ clientId = null, isAdminView = false, showHeader = true, initialConversationId = null }) => {
    const { branding } = useBranding();
    const { currentAgency, user } = useCRM();
    const { addNotification, createNotification } = useNotifications();
    const [conversations, setConversations] = useState([]);
    const [selectedConversation, setSelectedConversation] = useState(null);
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [unreadCount, setUnreadCount] = useState(0);
    const messagesEndRef = useRef(null);
    const messagesContainerRef = useRef(null);
    const shouldAutoScrollRef = useRef(false);
    const [showComposer, setShowComposer] = useState(false);
    const [composerRecipient, setComposerRecipient] = useState(null);
    const [showAppointmentModal, setShowAppointmentModal] = useState(false);
    const [appointmentData, setAppointmentData] = useState({
        title: '',
        description: '',
        startAt: '',
        endAt: '',
        location: '',
        type: 'appointment'
    });
    const [appointments, setAppointments] = useState([]);
    const [showEmojiPicker, setShowEmojiPicker] = useState(false);
    // Use global message tracking (shared with global polling hook)
    const lastMessageIdsRef = useRef(globalMessageTracking.lastMessageIds);
    const pollingIntervalRef = useRef(null);
    const conversationsRef = useRef([]);
    const selectedConversationRef = useRef(null);
    const isMountedRef = useRef(true);

    const personalizedColors = {
        primary: branding?.primaryColor || '#3B82F6',
        secondary: branding?.secondaryColor || '#1E40AF',
        accent: branding?.accentColor || '#F59E0B'
    };

    // Track if conversations have been loaded for current clientId/isAdminView combo
    const conversationsLoadedRef = useRef({ clientId: null, isAdminView: null });

    // Handle initial conversation selection from URL parameter
    useEffect(() => {
        if (initialConversationId && conversations.length > 0 && !selectedConversation) {
            const conversation = conversations.find(c => c.id === initialConversationId);
            if (conversation) {
                setSelectedConversation(conversation);
                if (process.env.NODE_ENV === 'development') {
                    console.log('[MessageList] Selecting initial conversation from URL:', initialConversationId);
                }
            }
        }
    }, [initialConversationId, conversations, selectedConversation]);

    // Track mounted state and clear global tracking on unmount
    useEffect(() => {
        isMountedRef.current = true;
        
        return () => {
            isMountedRef.current = false;
            // Always clear global tracking when component unmounts (e.g., user switches to another tab)
            // This ensures notifications show when user is not viewing messages
            const wasTracking = globalMessageTracking.selectedConversationId;
            globalMessageTracking.selectedConversationId = null;
            if (process.env.NODE_ENV === 'development') {
                console.log('[MessageList] Component unmounting - clearing selectedConversationId', {
                    wasTracking,
                    now: globalMessageTracking.selectedConversationId
                });
            }
        };
    }, []);

    useEffect(() => {
        if (selectedConversation && isMountedRef.current) {
            // Initialize tracking for this conversation if not already tracked
            if (!lastMessageIdsRef.current.has(selectedConversation.id)) {
                lastMessageIdsRef.current.set(selectedConversation.id, new Set());
            }
            selectedConversationRef.current = selectedConversation; // Keep ref in sync
            // Update global tracking so polling knows which conversation is being viewed
            // Only set if component is mounted
            globalMessageTracking.selectedConversationId = selectedConversation.id;
            if (process.env.NODE_ENV === 'development') {
                console.log('[MessageList] Setting selectedConversationId:', selectedConversation.id);
            }
            // Mark that we should auto-scroll on initial load
            shouldAutoScrollRef.current = true;
            loadMessages(selectedConversation.id, true); // Initial load, silent
        } else if (!selectedConversation) {
            selectedConversationRef.current = null;
            // Clear global tracking when conversation is deselected (only if still mounted)
            if (isMountedRef.current && globalMessageTracking.selectedConversationId) {
                globalMessageTracking.selectedConversationId = null;
                if (process.env.NODE_ENV === 'development') {
                    console.log('[MessageList] Clearing selectedConversationId - no conversation selected');
                }
            }
            // When conversation is deselected, don't clear trackedIds - we want to track across selections
        }
        
        // Cleanup: Clear global tracking when conversation changes
        // Note: Component unmount cleanup is handled in separate useEffect above
        return () => {
            // Store the conversation ID from the closure before it changes
            const previousConversationId = selectedConversation?.id;
            
            // If conversation changed (and component still mounted), only clear if it matches
            // If component unmounting, the other useEffect will handle it
            if (isMountedRef.current && previousConversationId && globalMessageTracking.selectedConversationId === previousConversationId) {
                // Conversation is changing, clear the old one
                globalMessageTracking.selectedConversationId = null;
                if (process.env.NODE_ENV === 'development') {
                    console.log('[MessageList] Conversation changing - clearing old selectedConversationId:', previousConversationId);
                }
            }
        };
    }, [selectedConversation]);

    // Mark messages as read when conversation is selected and messages are loaded
    useEffect(() => {
        if (selectedConversation && messages.length > 0) {
            // Get unread message IDs (messages not sent by current user)
            const getId = (value) => {
                if (!value) return null;
                if (typeof value === 'string') return value;
                if (value._id) return value._id.toString();
                if (value.toString) return value.toString();
                return null;
            };

            const unreadMessageIds = messages
                .filter(message => {
                    // Determine if message is from current user
                    let isCurrentUser = false;
                    if (isAdminView) {
                        const messageAgentId = getId(message.agentId);
                        const currentUserId = getId(user?._id);
                        isCurrentUser = message.senderType === 'agent' && messageAgentId === currentUserId;
                    } else {
                        const convClientId = selectedConversation?.id?.replace('conv_', '') || null;
                        const currentClientId = getId(convClientId || clientId || null);
                        const messageClientId = getId(message.clientId);
                        isCurrentUser = message.senderType === 'client' && messageClientId === currentClientId;
                    }
                    
                    // Check if message is already read by current user
                    const isRead = message.readBy?.some(r => {
                        const readerId = getId(r.userId || r);
                        const currentUserId = getId(user?._id);
                        return readerId === currentUserId;
                    });
                    
                    // Return true if message is not from current user and not read
                    return !isCurrentUser && !isRead;
                })
                .map(message => message._id);

            // Mark unread messages as read
            if (unreadMessageIds.length > 0) {
                messagesAPI.markAsRead(unreadMessageIds)
                    .then(() => {
                        // Reload conversations to update unread count
                        loadConversations();
                        loadUnreadCount();
                    })
                    .catch(error => {
                        console.error('Failed to mark messages as read:', error);
                    });
            }
        }
    }, [selectedConversation?.id, messages.length, isAdminView, user?._id, clientId]);

    // Polling for new messages in all conversations
    useEffect(() => {
        // Always set up polling, even if conversations haven't loaded yet
        // This ensures polling starts for admin users who might not have conversations initially
        
        // Update ref when conversations change
        conversationsRef.current = conversations;

        console.log('[Polling] Setting up polling effect:', {
            conversationsCount: conversations.length,
            isAdminView,
            clientId
        });

        // Set up polling interval (every 10 seconds)
        const pollMessages = () => {
            // Use ref to get latest conversations (avoid stale closures)
            const currentConversations = conversationsRef.current;
            
            console.log('[Polling] Starting poll cycle:', {
                conversationsCount: conversations.length,
                conversationsRefCount: currentConversations.length,
                isAdminView,
                clientId
            });
            
            if (!currentConversations.length) {
                console.log('[Polling] No conversations to poll - will retry on next cycle');
                return;
            }
            
            console.log('[Polling] Polling conversations:', currentConversations.map(c => ({ id: c.id, name: c.otherParticipant?.name })));
            currentConversations.forEach(conv => {
                if (conv?.id) {
                    console.log('[Polling] Polling conversation:', conv.id);
                    // Load messages for this conversation with silent=false to enable notifications
                    loadMessages(conv.id, false).catch(err => {
                        console.error(`[Polling] Failed to poll messages for conversation ${conv.id}:`, err);
                    });
                }
            });
        };
        
        // Poll immediately (after a short delay to let initial tracking complete)
        const initialPollTimeout = setTimeout(() => {
            pollMessages();
        }, 3000); // Wait 3 seconds for initial tracking to complete
        
        pollingIntervalRef.current = setInterval(() => {
            pollMessages();
        }, 10000); // 10 seconds

        return () => {
            clearTimeout(initialPollTimeout);
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
            }
        };
    }, [conversations.length, isAdminView, clientId]); // Include isAdminView and clientId to restart polling when these change


    useEffect(() => {
        if (selectedConversation || clientId) {
            loadAppointments();
        }
    }, [selectedConversation, clientId]);

    // Only auto-scroll when appropriate (new message from user, initial load, or if already near bottom)
    useEffect(() => {
        if (shouldAutoScrollRef.current) {
            scrollToBottom();
            shouldAutoScrollRef.current = false; // Reset after scrolling
        }
    }, [messages, appointments]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    // Check if user is near bottom of scroll container
    const isNearBottom = () => {
        const container = messagesContainerRef.current;
        if (!container) return true; // If container not found, assume at bottom
        
        const threshold = 100; // pixels from bottom
        const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight;
        return distanceFromBottom <= threshold;
    };

    const loadConversations = useCallback(async (showLoader = false) => {
        try {
            console.log('[MessageList] loadConversations called:', {
                clientId,
                isAdminView,
                showLoader
            });
            if (showLoader) {
                setLoading(true);
            }
            const res = await messagesAPI.getConversations(clientId);
            console.log('[MessageList] loadConversations response:', {
                success: res.data?.success,
                conversationsCount: res.data?.data?.length || 0,
                conversations: res.data?.data || []
            });
            if (res.data?.success) {
                const loadedConversations = res.data.data || [];
                
                // Only update state if conversations actually changed (compare IDs)
                const currentConversationIds = new Set(conversations.map(c => c.id));
                const newConversationIds = new Set(loadedConversations.map(c => c.id));
                const conversationsChanged = 
                    currentConversationIds.size !== newConversationIds.size ||
                    [...newConversationIds].some(id => !currentConversationIds.has(id));
                
                if (conversationsChanged) {
                    console.log('[MessageList] Conversations changed - updating state');
                    setConversations(loadedConversations);
                    
                    // Initialize tracking for NEW conversations only
                    // Pre-populate tracking by loading messages silently once for each new conversation
                    const initTracking = async () => {
                        await Promise.all(loadedConversations.map(async (conv) => {
                            if (conv?.id && !lastMessageIdsRef.current.has(conv.id)) {
                                lastMessageIdsRef.current.set(conv.id, new Set());
                                // Load messages silently once to initialize tracking
                                try {
                                    await loadMessages(conv.id, true);
                                } catch (err) {
                                    // Silently fail - this is just for initial tracking setup
                                    console.error(`Failed to initialize tracking for conversation ${conv.id}:`, err);
                                }
                            }
                        }));
                    };
                    // Don't wait for this - let it run in background
                    initTracking();
                } else {
                    console.log('[MessageList] Conversations unchanged - skipping state update');
                }
                
                // Always update refs and global tracking (for polling), even if state didn't change
                conversationsRef.current = loadedConversations; // Keep ref in sync
                globalMessageTracking.conversations = loadedConversations;
                
                // Auto-select first conversation if available
                if (loadedConversations.length > 0) {
                    // If locked to a specific client, pick that single conversation
                    const first = loadedConversations[0];
                    setSelectedConversation(first);
                } else if (selectedConversation && clientId) {
                    // Clear if no conversations for this client
                    setSelectedConversation(null);
                }
            }
        } catch (error) {
            console.error('Failed to load conversations:', error);
            toast.error('Failed to load conversations');
        } finally {
            if (showLoader) {
                setLoading(false);
            }
        }
    }, [clientId, isAdminView]);

    const loadMessages = async (conversationId, silent = false) => {
        try {
            console.log('[loadMessages] Called:', { conversationId, silent });
            const res = await messagesAPI.getMessages(conversationId, { clientId });
            if (res.data?.success) {
                const newMessages = res.data.data || [];
                console.log('[loadMessages] Received messages:', { 
                    conversationId, 
                    silent, 
                    messageCount: newMessages.length,
                    messageIds: newMessages.map(m => m._id) 
                });
                
                // Get or initialize tracked message IDs for this conversation
                // Always use the global tracking directly to ensure synchronization
                if (!globalMessageTracking.lastMessageIds.has(conversationId)) {
                    globalMessageTracking.lastMessageIds.set(conversationId, new Set());
                }
                // Update ref to point to global tracking (in case it changed)
                lastMessageIdsRef.current = globalMessageTracking.lastMessageIds;
                const trackedIds = globalMessageTracking.lastMessageIds.get(conversationId);
                console.log('[loadMessages] Tracking state:', { 
                    conversationId, 
                    silent, 
                    trackedIdsSize: trackedIds.size,
                    trackedIds: Array.from(trackedIds),
                    usingGlobalTracking: true,
                    globalTrackingHasConversation: globalMessageTracking.lastMessageIds.has(conversationId),
                    isSameReference: lastMessageIdsRef.current === globalMessageTracking.lastMessageIds
                });
                
                // Detect new messages (only when polling, not initial load)
                // If we have tracked message IDs, check for new ones
                // If we don't have tracked IDs yet, this is the first load - track them but don't notify
                if (!silent && newMessages.length > 0) {
                    console.log('[loadMessages] Checking for new messages (not silent):', {
                        conversationId,
                        newMessagesCount: newMessages.length,
                        trackedIdsSize: trackedIds.size
                    });
                    const currentMessageIds = new Set(newMessages.map(m => m._id));
                    
                    // Only check for new messages if we have previous tracking data
                    // If trackedIds is empty, this is the first time loading - just track the IDs
                    if (trackedIds.size === 0) {
                        // First time loading - track all message IDs but don't notify
                        // This happens when a conversation is first encountered during polling
                        console.log('[loadMessages] First time loading - tracking all messages (no notifications):', {
                            conversationId,
                            messageCount: newMessages.length
                        });
                        newMessages.forEach(m => trackedIds.add(m._id));
                    } else {
                        // We have previous tracking - check for new messages
                        const newMessageIds = [...currentMessageIds].filter(id => !trackedIds.has(id));
                        console.log('[loadMessages] Checking for new messages:', {
                            conversationId,
                            currentMessageIdsCount: currentMessageIds.size,
                            trackedIdsSize: trackedIds.size,
                            newMessageIdsCount: newMessageIds.length,
                            newMessageIds
                        });
                        
                        if (newMessageIds.length > 0) {
                            console.log('[loadMessages] ✅ New messages detected!', {
                                conversationId,
                                newMessageIdsCount: newMessageIds.length,
                                newMessageIds
                            });
                            // Find the actual new messages
                            const actualNewMessages = newMessages.filter(m => newMessageIds.includes(m._id));
                            console.log('[loadMessages] Actual new messages to process:', {
                                conversationId,
                                actualNewMessagesCount: actualNewMessages.length,
                                actualNewMessages: actualNewMessages.map(m => ({ id: m._id, senderType: m.senderType, agentId: m.agentId, clientId: m.clientId }))
                            });
                            
                            // Only notify for messages not sent by current user
                            actualNewMessages.forEach(message => {
                                // Helper to safely get ID from object or string
                                const getId = (value) => {
                                    if (!value) return null;
                                    if (typeof value === 'string') return value;
                                    if (value._id) return value._id.toString();
                                    if (value.toString) return value.toString();
                                    return null;
                                };
                                
                                // Determine if message is from current user
                                let isCurrentUser = false;
                                if (isAdminView) {
                                    // In admin view: current user is agent, check if message is from this agent
                                    const messageAgentId = getId(message.agentId);
                                    const currentUserId = getId(user?._id);
                                    isCurrentUser = message.senderType === 'agent' && messageAgentId === currentUserId;
                                } else {
                                    // In client view: current user is client, check if message is from this client
                                    // Get client ID from conversation or clientId prop
                                    const convClientId = conversationId?.replace('conv_', '') || null;
                                    const currentClientId = getId(convClientId || clientId || null);
                                    const messageClientId = getId(message.clientId);
                                    isCurrentUser = message.senderType === 'client' && messageClientId === currentClientId;
                                }
                                
                                // Don't notify if user is currently viewing this conversation
                                // Use refs to get latest state (avoid stale closures)
                                const currentSelectedConversation = selectedConversationRef.current;
                                const isViewingConversation = currentSelectedConversation?.id === conversationId;
                                
                                // Debug logging - detailed notification check
                                const willNotify = !isCurrentUser && !isViewingConversation;
                                console.log('[Notification Check] Detailed:', {
                                    conversationId,
                                    messageId: message._id,
                                    senderType: message.senderType,
                                    messageAgentId: getId(message.agentId),
                                    messageClientId: getId(message.clientId),
                                    currentUserId: getId(user?._id),
                                    isAdminView,
                                    isCurrentUser,
                                    isViewingConversation,
                                    willNotify,
                                    selectedConversationId: selectedConversationRef.current?.id,
                                    clientId,
                                    conversationClientId: conversationId?.replace('conv_', '') || null
                                });
                                
                                // Always log the willNotify decision
                                if (willNotify) {
                                    console.log('[Notification Check] ✅ Will notify - creating notification');
                                    
                                    // Only create notification if willNotify is true
                                    try {
                                        // Find the conversation to get sender name
                                        // Use ref to get latest conversations state (avoid stale closures)
                                        const currentConversations = conversationsRef.current;
                                        let conversation = currentConversations.find(c => c.id === conversationId);
                                        
                                        // If still not found, try selectedConversation if it matches
                                        if (!conversation && currentSelectedConversation?.id === conversationId) {
                                            conversation = currentSelectedConversation;
                                        }
                                        let senderName = '';
                                        if (isAdminView) {
                                            // In admin view: other participant is the client
                                            senderName = message.senderType === 'client' 
                                                ? conversation?.otherParticipant?.name || message.senderName || message.senderId?.name || 'Client'
                                                : message.senderName || message.senderId?.name || 'Team Member';
                                        } else {
                                            // In client view: other participant is the agent, client messages are from self
                                            senderName = message.senderType === 'agent' 
                                                ? conversation?.otherParticipant?.name || message.senderName || message.senderId?.name || 'Team Member'
                                                : message.senderName || message.senderId?.name || 'Client';
                                        }
                                        
                                        const conversationToSelect = conversation || currentSelectedConversation;
                                        
                                        // Create notification
                                        const notificationTitle = `New message from ${senderName}`;
                                        const notificationMessage = message.content?.substring(0, 100) + (message.content?.length > 100 ? '...' : '') || 'New message';
                                        
                                        console.log('[Notification Creation] Creating notification:', {
                                            title: notificationTitle,
                                            message: notificationMessage,
                                            type: 'client',
                                            conversationId,
                                            createNotificationAvailable: !!createNotification,
                                            createNotificationInfoAvailable: !!createNotification?.info
                                        });
                                        
                                        if (!createNotification) {
                                            console.error('[Notification Creation] ❌ createNotification is not available!');
                                            return;
                                        }
                                        
                                        if (!createNotification.info) {
                                            console.error('[Notification Creation] ❌ createNotification.info is not available!');
                                            return;
                                        }
                                        
                                        console.log('[Notification Creation] Calling createNotification.info...');
                                        createNotification.info(
                                            notificationTitle,
                                            notificationMessage,
                                            {
                                                type: 'client',
                                                onClick: () => {
                                                    console.log('[Notification Creation] Notification clicked - selecting conversation:', conversationToSelect?.id);
                                                    // Select the conversation when notification is clicked
                                                    if (conversationToSelect) {
                                                        setSelectedConversation(conversationToSelect);
                                                    }
                                                }
                                            }
                                        );
                                        console.log('[Notification Creation] ✅ createNotification.info called successfully');
                                        
                                        // Also show a toast
                                        toast.success(notificationTitle, {
                                            icon: '💬',
                                        });
                                    } catch (error) {
                                        console.error('[Notification Check] Error creating notification:', error);
                                    }
                                } else {
                                    console.log('[Notification Check] ❌ Will NOT notify:', {
                                        reason: isCurrentUser ? 'Message is from current user' : isViewingConversation ? 'User is viewing conversation' : 'Unknown reason',
                                        isCurrentUser,
                                        isViewingConversation
                                    });
                                    // Don't create notification - continue to next message in forEach
                                }
                            });
                            
                            // Update the tracked message IDs for this conversation
                            // Using global tracking directly - this syncs with useMessagePolling
                            trackedIds.clear();
                            newMessages.forEach(m => trackedIds.add(m._id));
                            console.log('[loadMessages] Updated tracking after new messages:', {
                                conversationId,
                                trackedIdsSize: trackedIds.size,
                                isGlobalTracking: trackedIds === globalMessageTracking.lastMessageIds.get(conversationId)
                            });
                        } else {
                            // No new messages, but update tracking in case order changed
                            trackedIds.clear();
                            newMessages.forEach(m => trackedIds.add(m._id));
                            console.log('[loadMessages] Updated tracking (no new messages):', {
                                conversationId,
                                trackedIdsSize: trackedIds.size,
                                isGlobalTracking: trackedIds === globalMessageTracking.lastMessageIds.get(conversationId)
                            });
                        }
                    }
                } else {
                    // Silent mode or no messages - just update tracking
                    trackedIds.clear();
                    newMessages.forEach(m => trackedIds.add(m._id));
                    console.log('[loadMessages] Updated tracking (silent mode):', {
                        conversationId,
                        trackedIdsSize: trackedIds.size,
                        isGlobalTracking: trackedIds === globalMessageTracking.lastMessageIds.get(conversationId)
                    });
                }
                
                // Only update displayed messages if this is the selected conversation
                if (selectedConversation?.id === conversationId) {
                    // Sort messages by date (oldest first)
                    const sortedMessages = [...newMessages].sort((a, b) => {
                        const dateA = new Date(a.createdAt || a.sentAt);
                        const dateB = new Date(b.createdAt || b.sentAt);
                        return dateA - dateB;
                    });
                    
                    // Only update state if messages actually changed (compare IDs and content)
                    let shouldUpdate = false;
                    let hasNewMessages = false;
                    
                    // First check if arrays are different lengths
                    if (messages.length !== sortedMessages.length) {
                        // Different lengths - definitely changed
                        shouldUpdate = true;
                        // Check if there are new messages (not just removed messages)
                        const currentMessageIds = new Set(messages.map(m => m._id));
                        const newMessageIds = new Set(sortedMessages.map(m => m._id));
                        hasNewMessages = [...newMessageIds].some(id => !currentMessageIds.has(id));
                    } else if (messages.length === 0 && sortedMessages.length === 0) {
                        // Both empty - no change, skip update
                        shouldUpdate = false;
                    } else {
                        // Same length - check if IDs match
                        const currentMessageIds = new Set(messages.map(m => m._id));
                        const newMessageIds = new Set(sortedMessages.map(m => m._id));
                        
                        // Check if all IDs match
                        const allIdsMatch = 
                            currentMessageIds.size === newMessageIds.size &&
                            [...newMessageIds].every(id => currentMessageIds.has(id));
                        
                        if (allIdsMatch) {
                            // Same IDs - check if any message content changed
                            const messagesById = new Map(messages.map(m => [m._id, m]));
                            const anyContentChanged = sortedMessages.some(newMsg => {
                                const oldMsg = messagesById.get(newMsg._id);
                                if (!oldMsg) return true;
                                // Compare relevant fields that might change
                                return (
                                    oldMsg.content !== newMsg.content ||
                                    oldMsg.status !== newMsg.status ||
                                    oldMsg.readBy?.length !== newMsg.readBy?.length
                                );
                            });
                            
                            if (anyContentChanged) {
                                shouldUpdate = true;
                            } else {
                                // No changes at all - skip update to prevent re-render
                                shouldUpdate = false;
                            }
                        } else {
                            // IDs don't match - messages changed
                            shouldUpdate = true;
                            hasNewMessages = [...newMessageIds].some(id => !currentMessageIds.has(id));
                        }
                    }
                    
                    // Only update state if messages actually changed
                    if (shouldUpdate) {
                        // Determine if we should auto-scroll:
                        // 1. If silent mode (initial load) - always scroll
                        // 2. If new messages detected and user is near bottom - scroll
                        // 3. If new messages detected but user scrolled up - don't scroll (preserve position)
                        if (silent) {
                            // Initial load - always scroll to bottom
                            shouldAutoScrollRef.current = true;
                        } else if (hasNewMessages) {
                            // New messages detected - only scroll if user is near bottom
                            const wasNearBottom = isNearBottom();
                            if (wasNearBottom) {
                                shouldAutoScrollRef.current = true;
                            }
                        }
                        // If no new messages or user scrolled up, don't auto-scroll (preserve position)
                        
                        setMessages(sortedMessages);
                    } else {
                        // No changes - preserve scroll position and don't update state
                        if (process.env.NODE_ENV === 'development') {
                            console.log('[loadMessages] Messages unchanged - skipping state update');
                        }
                    }
                }
            }
        } catch (error) {
            console.error('Failed to load messages:', error);
            if (!silent) {
            toast.error('Failed to load messages');
            }
        }
    };

    const loadUnreadCount = useCallback(async () => {
        try {
            const res = await messagesAPI.getUnreadCount(clientId);
            if (res.data?.success) {
                setUnreadCount(res.data.count || 0);
            }
        } catch (error) {
            console.error('Failed to load unread count:', error);
        }
    }, [clientId]);

    // Load conversations on mount or when clientId/isAdminView changes
    useEffect(() => {
        // Check if we've already loaded for this combination
        const hasLoaded = 
            conversationsLoadedRef.current.clientId === clientId &&
            conversationsLoadedRef.current.isAdminView === isAdminView;
        
        // Only load if clientId or isAdminView actually changed
        if (!hasLoaded) {
            if (process.env.NODE_ENV === 'development') {
                console.log('[MessageList] Loading conversations for:', {
                    clientId,
                    isAdminView,
                    initialConversationId
                });
            }
            
            // Mark as loaded
            conversationsLoadedRef.current = { clientId, isAdminView };
            
            // Show loader only on initial load
            loadConversations(true); // true = show loader
            loadUnreadCount();
        } else if (process.env.NODE_ENV === 'development') {
            console.log('[MessageList] Skipping duplicate load for:', { clientId, isAdminView });
        }
    }, [clientId, isAdminView, loadConversations, loadUnreadCount]);

    // Poll for conversations list to detect new conversations with messages
    // Note: We use a longer interval since global polling already handles message updates
    useEffect(() => {
        if (!conversations.length) return;

        const conversationsPollingInterval = setInterval(() => {
            // Only poll conversations (not messages) - global polling handles messages
            // Don't show loader during polling
            loadConversations(false); // false = don't show loader
            loadUnreadCount();
        }, 60000); // Check conversations every 60 seconds (less frequent since global polling exists)

        return () => {
            clearInterval(conversationsPollingInterval);
        };
    }, [conversations.length, loadConversations, loadUnreadCount]);

    const loadAppointments = async () => {
        try {
            const convClientId = getClientIdFromConversation();
            const targetClientId = convClientId || clientId;
            
            if (!targetClientId) {
                setAppointments([]);
                return;
            }

            const res = await calendarAPI.getEvents({ 
                clientId: targetClientId,
                type: 'appointment'
            });
            
            if (res.data?.success) {
                setAppointments(res.data.data || []);
            } else {
                setAppointments([]);
            }
        } catch (error) {
            console.error('Failed to load appointments:', error);
            setAppointments([]);
        }
    };

    const handleSendMessage = async () => {
        if (!newMessage.trim() || !selectedConversation) return;

        try {
            setSending(true);
            const messageData = {
                content: newMessage.trim(),
                conversationId: selectedConversation.id,
                recipientId: selectedConversation.otherParticipant.id,
                recipientModel: selectedConversation.otherParticipant.model
            };

            const res = await messagesAPI.sendMessage(messageData);
            if (res.data?.success) {
                setNewMessage('');
                // Reload messages to show the new one (silent, as it's from user's own action)
                // Mark that we should auto-scroll after sending message
                shouldAutoScrollRef.current = true;
                await loadMessages(selectedConversation.id, true);
                await loadUnreadCount();
                toast.success('Message sent');
            } else {
                throw new Error(res.data?.message || 'Failed to send message');
            }
        } catch (error) {
            console.error('Failed to send message:', error);
            toast.error('Failed to send message');
        } finally {
            setSending(false);
        }
    };

    const handleKeyPress = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    const formatTime = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diffInHours = (now - date) / (1000 * 60 * 60);

        if (diffInHours < 24) {
            return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        } else if (diffInHours < 168) { // 7 days
            return date.toLocaleDateString([], { weekday: 'short', hour: '2-digit', minute: '2-digit' });
        } else {
            return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
        }
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return '';
        return date.toLocaleString([], { 
            month: 'short', 
            day: 'numeric', 
            hour: '2-digit', 
            minute: '2-digit' 
        });
    };

    // Combine messages and appointments, sorted by date - memoized to prevent unnecessary recalculations
    const timeline = useMemo(() => {
        const timelineItems = [];
        
        // Add messages
        messages.forEach(msg => {
            timelineItems.push({
                id: msg._id,
                type: 'message',
                data: msg,
                timestamp: new Date(msg.createdAt || msg.sentAt)
            });
        });
        
        // Add appointments
        appointments.forEach(apt => {
            timelineItems.push({
                id: apt._id,
                type: 'appointment',
                data: apt,
                timestamp: new Date(apt.startAt || apt.createdAt)
            });
        });
        
        // Sort by timestamp (oldest first)
        return timelineItems.sort((a, b) => a.timestamp - b.timestamp);
    }, [messages, appointments]);

    const getMessageStatusIcon = (message) => {
        // Fallback: use status field only to avoid null ID issues
        if (message?.status === 'read') {
            return <CheckCircleIcon className="h-4 w-4 text-blue-500" />;
        }
        if (message?.status === 'sent') {
            return <CheckIcon className="h-4 w-4 text-muted-foreground" />;
        }
        return null;
    };

    const startNewConversation = (participant) => {
        setComposerRecipient(participant);
        setShowComposer(true);
    };

    // Extract client ID from conversation
    const getClientIdFromConversation = () => {
        if (!selectedConversation) return null;
        if (selectedConversation.id && selectedConversation.id.startsWith('conv_')) {
            return selectedConversation.id.replace('conv_', '');
        }
        // Fallback: try to get from clientId prop
        return clientId || null;
    };

    const handleCreateAppointment = () => {
        const convClientId = getClientIdFromConversation();
        if (!convClientId && !clientId) {
            toast.error('No client selected');
            return;
        }

        const now = new Date();
        const tomorrow = new Date(now);
        tomorrow.setDate(tomorrow.getDate() + 1);
        tomorrow.setHours(10, 0, 0, 0);
        
        const endTime = new Date(tomorrow);
        endTime.setHours(11, 0, 0, 0);

        setAppointmentData({
            title: `Appointment with ${selectedConversation?.otherParticipant?.name || 'Client'}`,
            description: '',
            startAt: tomorrow.toISOString().slice(0, 16),
            endAt: endTime.toISOString().slice(0, 16),
            location: '',
            type: 'appointment'
        });
        setShowAppointmentModal(true);
    };

    const handleSaveAppointment = async () => {
        try {
            const convClientId = getClientIdFromConversation();
            const targetClientId = convClientId || clientId;

            if (!targetClientId) {
                toast.error('No client selected');
                return;
            }

            if (!appointmentData.title.trim()) {
                toast.error('Appointment title is required');
                return;
            }

            if (!appointmentData.startAt || !appointmentData.endAt) {
                toast.error('Start and end times are required');
                return;
            }

            const eventData = {
                ...appointmentData,
                clientId: targetClientId,
                startAt: new Date(appointmentData.startAt).toISOString(),
                endAt: new Date(appointmentData.endAt).toISOString(),
                type: 'appointment',
                status: 'scheduled'
            };

            await calendarAPI.create(eventData);
            toast.success('Appointment created successfully!');
            setShowAppointmentModal(false);
            setAppointmentData({
                title: '',
                description: '',
                startAt: '',
                endAt: '',
                location: '',
                type: 'appointment'
            });
            // Reload appointments to show the new one
            await loadAppointments();
        } catch (error) {
            console.error('Failed to create appointment:', error);
            toast.error('Failed to create appointment');
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2" style={{ borderColor: personalizedColors.primary }}></div>
            </div>
        );
    }

    return (
        <div className="h-full flex flex-col bg-card rounded-lg border border-border">
            {/* Header */}
            {showHeader && (
            <div className="flex items-center justify-between p-4 border-b border-border bg-muted">
                <div className="flex items-center space-x-3">
                    <ChatBubbleLeftRightIcon className="h-6 w-6" style={{ color: personalizedColors.primary }} />
                    <div>
                        <h2 className="text-lg font-semibold text-foreground">Messages</h2>
                        {unreadCount > 0 && (
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800">
                                {unreadCount} unread
                            </span>
                        )}
                    </div>
                </div>
                {isAdminView && !clientId && (
                    <Button
                        onClick={() => setShowComposer(true)}
                        className="text-sm"
                        style={{ backgroundColor: personalizedColors.primary }}
                    >
                        New Message
                    </Button>
                )}
            </div>
            )}

            <div className="flex flex-1 overflow-hidden">
                {/* Conversations List */}
                <div className="w-80 border-r border-border flex flex-col">
                    <div className="p-4 border-b border-border">
                        <h3 className="text-sm font-medium text-foreground">Conversations</h3>
                    </div>
                    <div className="flex-1 overflow-y-auto scrollbar-thin">
                        {conversations.length === 0 ? (
                            <div className="p-4 text-center text-muted-foreground">
                                <ChatBubbleLeftRightIcon className="h-12 w-12 mx-auto mb-2 text-muted-foreground/50" />
                                <p className="text-sm">No conversations yet</p>
                                {isAdminView && (
                                    <p className="text-xs text-muted-foreground/70 mt-1">Start a conversation with a client</p>
                                )}
                            </div>
                        ) : (
                            <div className="space-y-1">
                                {conversations.map((conversation) => (
                                    <button
                                        key={conversation.id}
                                        onClick={() => setSelectedConversation(conversation)}
                                        className={`w-full text-left p-4 hover:bg-muted transition-colors ${
                                            selectedConversation?.id === conversation.id ? 'bg-primary/10 border-r-2' : ''
                                        }`}
                                        style={{
                                            borderRightColor: selectedConversation?.id === conversation.id ? personalizedColors.primary : 'transparent'
                                        }}
                                    >
                                        <div className="flex items-center space-x-3">
                                            <div className="flex-shrink-0">
                                                {conversation.otherParticipant?.profileImage ? (
                                                    <img
                                                        src={conversation.otherParticipant.profileImage}
                                                        alt={conversation.otherParticipant.name}
                                                        className="w-10 h-10 rounded-full object-cover border border-border"
                                                    />
                                                ) : (
                                                    <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center">
                                                        <span className="text-xs font-medium text-primary-foreground">
                                                            {conversation.otherParticipant?.name?.charAt(0)?.toUpperCase() || 'U'}
                                                        </span>
                                                </div>
                                                )}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="flex items-center justify-between">
                                                    <p className="text-sm font-medium text-foreground truncate">
                                                        {conversation.otherParticipant.name}
                                                    </p>
                                                    {conversation.unreadCount > 0 && (
                                                        <span className="inline-flex items-center justify-center w-5 h-5 text-xs font-bold text-white bg-red-500 rounded-full">
                                                            {conversation.unreadCount}
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-xs text-muted-foreground truncate">
                                                    {conversation.lastMessage?.content || 'No messages yet'}
                                                </p>
                                                <p className="text-xs text-muted-foreground/70">
                                                    {conversation.lastMessage ? formatTime(conversation.lastMessage.createdAt) : ''}
                                                </p>
                                            </div>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Messages Area */}
                <div className="flex-1 flex flex-col">
                    {selectedConversation ? (
                        <>
                            {/* Messages Header */}
                            <div className="p-4 border-b border-border bg-muted">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-3">
                                        <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                                            <ChatBubbleLeftRightIcon className="h-4 w-4 text-primary-foreground" />
                                        </div>
                                        <div className="flex-1">
                                            <h3 className="text-sm font-medium text-foreground">
                                                {selectedConversation.otherParticipant.name}
                                            </h3>
                                            <p className="text-xs text-muted-foreground">
                                                {selectedConversation.otherParticipant.email}
                                            </p>
                                        </div>
                                    </div>
                                    <button
                                        onClick={handleCreateAppointment}
                                        className="inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium rounded-lg border border-input bg-background text-foreground hover:bg-muted transition-colors focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                                        title="Create Appointment"
                                    >
                                        <CalendarIcon className="h-3.5 w-3.5" style={{ color: personalizedColors.primary }} />
                                        <span className="hidden sm:inline">Appointment</span>
                                    </button>
                                </div>
                            </div>

                            {/* Messages List */}
                            <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-4 space-y-4 scrollbar-thin">
                                {(() => {
                                    if (timeline.length === 0) {
                                        return (
                                    <div className="text-center text-muted-foreground py-8">
                                        <ChatBubbleLeftRightIcon className="h-12 w-12 mx-auto mb-2 text-muted-foreground/50" />
                                                <p className="text-sm">No messages or appointments in this conversation</p>
                                            </div>
                                        );
                                    }
                                    
                                    return timeline.map((item) => {
                                        if (item.type === 'appointment') {
                                            const apt = item.data;
                                            return (
                                                <div key={`apt-${item.id}`} className="flex justify-start">
                                                    <div className="max-w-xs lg:max-w-md px-4 py-3 rounded-lg bg-green-50 dark:bg-green-950/20 border border-green-200 dark:border-green-800">
                                                        <div className="flex items-start gap-3">
                                                            <div className="flex-shrink-0 mt-0.5">
                                                                <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: `${personalizedColors.primary}20` }}>
                                                                    <CalendarIcon className="h-4 w-4" style={{ color: personalizedColors.primary }} />
                                                                </div>
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <div className="flex items-center gap-2 mb-1">
                                                                    <span className="text-xs font-medium text-green-700 dark:text-green-300">Appointment</span>
                                                                    <span className="text-xs text-green-600 dark:text-green-400">
                                                                        {formatDateTime(apt.startAt)}
                                                                    </span>
                                                                </div>
                                                                <h4 className="text-sm font-medium text-foreground mb-1">
                                                                    {apt.title}
                                                                </h4>
                                                                {apt.description && (
                                                                    <p className="text-xs text-muted-foreground mb-2">
                                                                        {apt.description}
                                                                    </p>
                                                                )}
                                                                {apt.location && (
                                                                    <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                                                        <MapPinIcon className="h-3 w-3" />
                                                                        <span>{apt.location}</span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        </div>
                                                    </div>
                                    </div>
                                            );
                                        } else {
                                            const message = item.data;
                                            
                                            // Helper to safely get ID from object or string
                                            const getId = (value) => {
                                                if (!value) return null;
                                                if (typeof value === 'string') return value;
                                                if (value._id) return value._id.toString();
                                                if (value.toString) return value.toString();
                                                return null;
                                            };
                                            
                                            // Determine if message is from current user
                                            let isCurrentUser = false;
                                            if (isAdminView) {
                                                // In admin view: current user is agent, check if message is from this agent
                                                const messageAgentId = getId(message.agentId);
                                                const currentUserId = getId(user?._id);
                                                isCurrentUser = message.senderType === 'agent' && messageAgentId === currentUserId;
                                            } else {
                                                // In client view: current user is client, check if message is from this client
                                                // Get client ID from conversation or clientId prop
                                                const convClientId = selectedConversation?.id?.replace('conv_', '') || null;
                                                const currentClientId = getId(convClientId || clientId || null);
                                                const messageClientId = getId(message.clientId);
                                                isCurrentUser = message.senderType === 'client' && messageClientId === currentClientId;
                                            }
                                            
                                            // Get sender info for avatar
                                            const sender = message.senderId || {};
                                            const senderName = sender.name || message.senderName || (message.senderType === 'client' ? 'Client' : 'Team Member');
                                            
                                            // Get avatar: prefer senderId.profileImage, fall back to conversation's otherParticipant
                                            let senderAvatar = sender.profileImage || null;
                                            if (!senderAvatar && message.senderType === 'client' && isAdminView) {
                                                // In admin view, otherParticipant is the client
                                                senderAvatar = selectedConversation?.otherParticipant?.profileImage || null;
                                            }
                                            if (!senderAvatar && message.senderType === 'agent' && !isAdminView) {
                                                // In client view, otherParticipant is the agent
                                                senderAvatar = selectedConversation?.otherParticipant?.profileImage || null;
                                            }
                                            
                                            return (
                                    <div
                                            key={message._id}
                                                    className={`flex items-end gap-2 ${isCurrentUser ? 'justify-end' : 'justify-start'}`}
                                                >
                                                    {/* Avatar - show on left for received messages, on right for sent messages */}
                                                    {!isCurrentUser && (
                                                        <div className="flex-shrink-0 mb-1">
                                                            {senderAvatar ? (
                                                                <img
                                                                    src={senderAvatar}
                                                                    alt={senderName}
                                                                    className="h-8 w-8 rounded-full object-cover border border-border"
                                                                />
                                                            ) : (
                                                                <div className="h-8 w-8 bg-primary rounded-full flex items-center justify-center">
                                                                    <span className="text-xs font-medium text-primary-foreground">
                                                                        {senderName.charAt(0).toUpperCase() || 'U'}
                                                                    </span>
                                                                </div>
                                                            )}
                                                        </div>
                                                    )}
                                                    
                                            <div
                                                className={`max-w-xs lg:max-w-md px-4 py-2 rounded-lg ${
                                                            isCurrentUser
                                                                ? 'text-white'
                                                                : 'bg-muted text-foreground'
                                                }`}
                                                style={{
                                                            backgroundColor: isCurrentUser 
                                                                ? personalizedColors.primary 
                                                                : undefined
                                                        }}
                                                    >
                                                        {!isCurrentUser && (
                                                            <p className="text-xs font-medium text-muted-foreground mb-1">
                                                                {senderName}
                                                            </p>
                                                        )}
                                                <p className="text-sm">{message.content}</p>
                                                        <div className={`flex items-center justify-between mt-1 ${isCurrentUser ? '' : 'flex-row-reverse'}`}>
                                                    <span className="text-xs opacity-70">
                                                                {formatTime(message.createdAt || message.sentAt)}
                                                            </span>
                                                            {isCurrentUser && getMessageStatusIcon(message)}
                                                        </div>
                                                    </div>
                                                    
                                                    {/* Avatar - show on right for sent messages */}
                                                    {isCurrentUser && (
                                                        <div className="flex-shrink-0 mb-1">
                                                            {(() => {
                                                                // For current user avatar, use user.profileImage in admin view, or conversation's client profileImage in client view
                                                                let currentUserAvatar = null;
                                                                let currentUserName = 'You';
                                                                
                                                                if (isAdminView) {
                                                                    currentUserAvatar = user?.profileImage || null;
                                                                    currentUserName = user?.name || 'You';
                                                                } else {
                                                                    // In client view, current user is the client
                                                                    // For client's own messages, use the current message's senderId.profileImage
                                                                    // Since this is rendering the current user's own message, message.senderId should have the profileImage
                                                                    currentUserAvatar = message.senderId?.profileImage || null;
                                                                    currentUserName = message.senderId?.name || 'You';
                                                                }
                                                                
                                                                return currentUserAvatar ? (
                                                                    <img
                                                                        src={currentUserAvatar}
                                                                        alt={currentUserName}
                                                                        className="h-8 w-8 rounded-full object-cover border border-border"
                                                                    />
                                                                ) : (
                                                                    <div className="h-8 w-8 bg-primary rounded-full flex items-center justify-center">
                                                                        <span className="text-xs font-medium text-primary-foreground">
                                                                            {currentUserName.charAt(0).toUpperCase() || 'U'}
                                                    </span>
                                                                    </div>
                                                                );
                                                            })()}
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        }
                                    });
                                })()}
                                <div ref={messagesEndRef} />
                            </div>

                            {/* Message Composer */}
                            <div className="p-4 border-t border-border bg-muted">
                                <div className="flex items-center space-x-2">
                                    <div className="flex-1 relative">
                                        <textarea
                                            value={newMessage}
                                            onChange={(e) => setNewMessage(e.target.value)}
                                            onKeyPress={handleKeyPress}
                                            placeholder="Type your message..."
                                            className="w-full px-3 py-2 pr-10 pb-8 border border-input b bg-background text-foregroundg-background text-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring resize-none"
                                            rows="2"
                                            disabled={sending}
                                        />
                                        <Popover open={showEmojiPicker} onOpenChange={setShowEmojiPicker}>
                                            <PopoverTrigger asChild>
                                                <button
                                                    type="button"
                                                    className="absolute bottom-2 left-2 p-1.5 text-muted-foreground hover:text-foreground hover:bg-muted rounded transition-colors"
                                                    title="Add emoji"
                                                >
                                                    <FaceSmileIcon className="h-4 w-4" />
                                                </button>
                                            </PopoverTrigger>
                                            <PopoverContent className="w-auto p-0 border-none shadow-lg" align="start" side="top">
                                                <EmojiPicker
                                                    onEmojiClick={(emojiData) => {
                                                        setNewMessage(prev => prev + emojiData.emoji);
                                                        setShowEmojiPicker(false);
                                                    }}
                                                    theme="auto"
                                                    previewConfig={{ showPreview: false }}
                                                    skinTonesDisabled
                                                />
                                            </PopoverContent>
                                        </Popover>
                                    </div>
                                    <button
                                        onClick={handleSendMessage}
                                        disabled={!newMessage.trim() || sending}
                                        className="inline-flex items-center justify-center h-10 px-4 py-2 text-sm font-medium text-white rounded-lg shadow-sm hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none"
                                        style={{ 
                                            backgroundColor: personalizedColors.primary,
                                            '--tw-ring-color': personalizedColors.primary
                                        }}
                                        title="Send message (Enter)"
                                    >
                                        {sending ? (
                                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                        ) : (
                                            <PaperAirplaneIcon className="h-4 w-4" />
                                        )}
                                    </button>
                                </div>
                            </div>
                        </>
                    ) : (
                        <div className="flex-1 flex items-center justify-center text-muted-foreground">
                            <div className="text-center">
                                <ChatBubbleLeftRightIcon className="h-12 w-12 mx-auto mb-2 text-muted-foreground/50" />
                                <p className="text-sm">{clientId ? 'No messages yet with this client' : 'Select a conversation to start messaging'}</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* New Message Composer Modal */}
            {showComposer && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-[9999]">
                    <div className="bg-card border border-border rounded-lg p-6 w-full max-w-md">
                        <h3 className="text-lg font-semibold text-foreground mb-4">New Message</h3>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-1">
                                    Recipient
                                </label>
                                <select
                                    value={composerRecipient?.id || ''}
                                    onChange={(e) => {
                                        const recipient = conversations.find(c => c.otherParticipant.id === e.target.value);
                                        setComposerRecipient(recipient?.otherParticipant || null);
                                    }}
                                    className="w-full px-3 py-2 border border-input b bg-background text-foregroundg-background text-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                >
                                    <option value="">Select recipient...</option>
                                    {conversations.map((conversation) => (
                                        <option key={conversation.otherParticipant.id} value={conversation.otherParticipant.id}>
                                            {conversation.otherParticipant.name} ({conversation.otherParticipant.email})
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-1">
                                    Message
                                </label>
                                <textarea
                                    value={newMessage}
                                    onChange={(e) => setNewMessage(e.target.value)}
                                    placeholder="Type your message..."
                                    className="w-full px-3 py-2 border border-input b bg-background text-foregroundg-background text-foreground rounded-md focus:outline-none focus:ring-2 focus:ring-ring f focus:ring-2 focus:ring-ring focus:border-ringocus:border-ring"
                                    rows="3"
                                />
                            </div>
                        </div>
                        <div className="flex justify-end space-x-2 mt-6">
                            <Button
                                onClick={() => {
                                    setShowComposer(false);
                                    setComposerRecipient(null);
                                    setNewMessage('');
                                }}
                                variant="outline"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={() => {
                                    if (composerRecipient && newMessage.trim()) {
                                        // Find or create conversation
                                        const existingConversation = conversations.find(
                                            c => c.otherParticipant.id === composerRecipient.id
                                        );
                                        if (existingConversation) {
                                            setSelectedConversation(existingConversation);
                                        }
                                        setShowComposer(false);
                                        handleSendMessage();
                                    }
                                }}
                                disabled={!composerRecipient || !newMessage.trim()}
                                style={{ backgroundColor: personalizedColors.primary }}
                            >
                                Send
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {/* Appointment Creation Modal */}
            {showAppointmentModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-[9999]" onClick={() => setShowAppointmentModal(false)}>
                    <div 
                        className="bg-card border border-border rounded-lg p-6 w-full max-w-md shadow-xl"
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                                <CalendarIcon className="h-5 w-5" style={{ color: personalizedColors.primary }} />
                                Create Appointment
                            </h3>
                            <button
                                onClick={() => setShowAppointmentModal(false)}
                                className="text-muted-foreground hover:text-foreground"
                            >
                                <span className="sr-only">Close</span>
                                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-1">
                                    Title <span className="text-red-500">*</span>
                                </label>
                                <input
                                    type="text"
                                    value={appointmentData.title}
                                    onChange={(e) => setAppointmentData({ ...appointmentData, title: e.target.value })}
                                    className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
                                    placeholder="Appointment title"
                                />
                            </div>
                            
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-1">
                                        Start <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="datetime-local"
                                        value={appointmentData.startAt}
                                        onChange={(e) => setAppointmentData({ ...appointmentData, startAt: e.target.value })}
                                        className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-foreground mb-1">
                                        End <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="datetime-local"
                                        value={appointmentData.endAt}
                                        onChange={(e) => setAppointmentData({ ...appointmentData, endAt: e.target.value })}
                                        className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
                                    />
                                </div>
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-1">
                                    Location (optional)
                                </label>
                                <input
                                    type="text"
                                    value={appointmentData.location}
                                    onChange={(e) => setAppointmentData({ ...appointmentData, location: e.target.value })}
                                    className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring"
                                    placeholder="Meeting location"
                                />
                            </div>
                            
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-1">
                                    Description (optional)
                                </label>
                                <textarea
                                    value={appointmentData.description}
                                    onChange={(e) => setAppointmentData({ ...appointmentData, description: e.target.value })}
                                    className="w-full px-3 py-2 border border-input rounded-md bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:border-ring resize-none"
                                    rows="3"
                                    placeholder="Additional notes..."
                                />
                            </div>
                        </div>
                        
                        <div className="flex justify-end gap-3 mt-6 pt-4 border-t border-border">
                            <Button
                                onClick={() => setShowAppointmentModal(false)}
                                variant="outline"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleSaveAppointment}
                                style={{ backgroundColor: personalizedColors.primary }}
                            >
                                Create Appointment
                            </Button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default MessageList;
